/**
 * Read-only client for canonical reference data (currently just ICD-10).
 * Endpoints declared in claims/openapi.yaml; types in generated.d.ts.
 */

import api from '../utils/axios';

/** @typedef {import('./generated').components['schemas']} Schemas */
/** @typedef {Schemas['Icd10CodeRef']} Icd10CodeRef */
/** @typedef {Schemas['Icd10CodeDetail']} Icd10CodeDetail */

/**
 * Prefix-search the canonical ICD-10 table.
 * Input is normalized server-side; passing "M54.5", "m545", or "M545"
 * all return the same results.
 *
 * @param {string} q  Code prefix (min length 1 after server-side normalization).
 * @param {number} [limit=25]  Max results (server caps at 50).
 * @returns {Promise<Icd10CodeRef[]>}
 */
export async function fetchIcd10ByPrefix(q, limit = 25) {
  if (!q || !q.trim()) return [];
  const { data } = await api.get('/claims/reference/icd10/', {
    params: { q: q.trim(), limit },
  });
  return Array.isArray(data?.results) ? data.results : [];
}

/**
 * Fetch a single ICD-10 code's canonical descriptions.
 * Returns ``null`` when the code is not in the active CMS list (404 from server).
 *
 * @param {string} code  ICD-10 code (with or without period; case-insensitive).
 * @returns {Promise<Icd10CodeDetail | null>}
 */
export async function fetchIcd10ByCode(code) {
  if (!code || !code.trim()) return null;
  try {
    const { data } = await api.get(`/claims/reference/icd10/${encodeURIComponent(code.trim())}/`);
    return data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}
